import type { AbhaMemberListDetail, AbhaSessionState } from '@helpers/abhaJourneyHelper'

export type DownloadAbhaCardAssetOptions = {
  cardVariant?: 'hideAbhaNumber' | 'showAbhaNumber'
  accountIndex?: number
}

const stripBase64Payload = (value: string) => {
  if (value.startsWith('data:')) {
    const commaIndex = value.indexOf(',')

    if (commaIndex !== -1) {
      return value.slice(commaIndex + 1)
    }
  }

  return value
}

const downloadBase64AsFile = (base64: string, fileName: string, mimeType = 'image/png') => {
  const payload = stripBase64Payload(base64)
  const byteCharacters = atob(payload)
  const byteNumbers = new Array(byteCharacters.length)

  for (let index = 0; index < byteCharacters.length; index += 1) {
    byteNumbers[index] = byteCharacters.charCodeAt(index)
  }

  const byteArray = new Uint8Array(byteNumbers)
  const blob = new Blob([byteArray], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = fileName
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

const triggerUrlDownload = (href: string, filename: string) => {
  const link = document.createElement('a')
  link.href = href
  link.download = filename
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const asRecord = (value: unknown) =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined

const asString = (value: unknown) => (typeof value === 'string' && value.length > 0 ? value : undefined)

const getMemberUserDetails = (member?: AbhaMemberListDetail) => asRecord(member?.abha_user_details)

const resolveDownloadFileName = (session: AbhaSessionState, member?: AbhaMemberListDetail) => {
  const memberUserDetails = getMemberUserDetails(member)
  const memberName = asRecord(member?.name)
  const sessionUserDetails = asRecord(session.abha_user_details)
  const sessionName = asRecord(sessionUserDetails?.name)

  const firstName =
    asString(memberUserDetails?.firstName) ??
    asString(memberUserDetails?.first_name) ??
    asString(memberName?.first_name) ??
    asString(sessionUserDetails?.firstName) ??
    asString(sessionUserDetails?.first_name) ??
    asString(sessionName?.first_name) ??
    'abha-card'

  return `${firstName.toLowerCase()}.png`
}

const getMemberCardPng = (member?: AbhaMemberListDetail) => asString(member?.abha_card_png)

const getMemberCard = (member?: AbhaMemberListDetail) => asString(member?.abha_card)

const downloadMemberCard = (card: string, fileName: string) => {
  const payload = stripBase64Payload(card)
  const isPdf = card.startsWith('data:application/pdf') || payload.startsWith('JVBERi0')

  downloadBase64AsFile(
    card,
    isPdf ? fileName.replace(/\.png$/, '.pdf') : fileName,
    isPdf ? 'application/pdf' : 'image/png',
  )
}

const downloadPngAsset = (png: string, fileName: string) => {
  if (png.startsWith('http://') || png.startsWith('https://')) {
    triggerUrlDownload(png, fileName)
    return
  }

  downloadBase64AsFile(png, fileName)
}

export const downloadAbhaCardAsset = (session?: AbhaSessionState | null, options?: DownloadAbhaCardAssetOptions) => {
  if (!session) {
    throw new Error('ABHA card is not available for download yet.')
  }

  const member = Number.isFinite(options?.accountIndex)
    ? session.abha_member_list_details?.find(
        item => Number(item?.account_index) === options?.accountIndex,
      )
    : session.abha_member_list_details?.[0]
  const fileName = resolveDownloadFileName(session, member)
  const isShowVariant = options?.cardVariant === 'showAbhaNumber'
  const memberCardPng = getMemberCardPng(member)
  const memberCard = getMemberCard(member)

  if (isShowVariant && memberCardPng) {
    downloadPngAsset(memberCardPng, fileName)
    return
  }

  if (isShowVariant && memberCard) {
    downloadMemberCard(memberCard, fileName)
    return
  }

  if (session.abha_card_png) {
    downloadPngAsset(session.abha_card_png, fileName)
    return
  }

  if (memberCardPng) {
    downloadPngAsset(memberCardPng, fileName)
    return
  }

  if (memberCard) {
    downloadMemberCard(memberCard, fileName)
    return
  }

  const cardAsset = session.abha_card

  if (typeof cardAsset === 'string') {
    downloadPngAsset(cardAsset, fileName)
    return
  }

  if (cardAsset && typeof cardAsset === 'object') {
    const record = cardAsset
    const png = asString(record.abha_card_png) ?? asString(record.png) ?? asString(record.image)

    if (png) {
      downloadPngAsset(png, fileName)
      return
    }

    const url = asString(record.url) ?? asString(record.download_url)

    if (url) {
      triggerUrlDownload(url, fileName)
      return
    }
  }

  throw new Error('ABHA card download is unavailable. Please try again later.')
}
